import { BOAT, OUTBOARD, INBOARD, SINGLE, MODIFIED_V, SEMI_V, TRI_HULL, V, ROUND_BILGE } from 'constants/boatApplication';
import moment from 'moment';
import { _initBoatSystems } from './BaseBoat';
import { AREA_TYPES } from 'constants/boatApplication';

const initState = {
  id: 'new',
  boatType: BOAT,
  status: 'DRAFT',
  engineType: null,
  boatSystems: [],
  cubicCapacity: {},
  floatationCapacity: {},
  additionalFiles: [],
};

export default class Boat {
  constructor(boat = initState) {
    this.id = boat.id || initState.id;
    this.boatType = BOAT;
    this.status = boat.status || initState.status;
    this.engineType = boat.engineType;
    this.plateType = boat.plateType;
    this.dualSteering = _initDualSteering.call(this, boat);
    this.boatSpecifications = _initBoatSpecifications.call(this, boat.boatSpecifications || boat);
    this.boatSystems = _initBoatSystems(boat.boatSystems);
    this.specificationsRequiredFieldsList = getSpecificationsRequiredFieldsList.call(this);
    this.cubicCapacity = _initCubicCapacity(boat.cubicCapacity);
    this.personPowerCapacity = _initPersonPowerCapacity.call(this, boat.personPowerCapacity ? {...boat.personPowerCapacity, dualSteering:boat.dualSteering} : boat);
    this.powerCapacity = _initPowerCapacity(boat.powerCapacity || boat);
    this.floatationCapacity = _initFloatationCapacity(boat.floatationCapacity);
    this.additionalFiles = boat.additionalFiles ? [...boat.additionalFiles] : initState.additionalFiles;
    this.isFloatationTestReportRequired = boat.boatSpecifications ? boat.boatSpecifications.lengthFeet < 20 : boat.lengthFeet < 20;
    this.bottomContourList = [
      {name: 'Modified V', value: MODIFIED_V},
      {name: 'Semi V', value: SEMI_V},
      {name: 'Tri Hull', value: TRI_HULL},
      {name: 'V', value: V},
      {name: 'Round Bilge', value: ROUND_BILGE}
    ];
    this.engineTypesList = [
      INBOARD,
      OUTBOARD
    ];
    this.engineSubTypesList = [
      {name: 'Up to 6 HP.', value: 'SUB_TYPE_1'},
      {name: 'Over 6 HP. and up to 20 HP.', value: 'SUB_TYPE_2'},
      {name: 'Over 20 HP. and up to 100 HP.', value: 'SUB_TYPE_3'},
      {name: 'Over 100 HP.', value: 'SUB_TYPE_4'}
    ];
  }
}

function getSpecificationsRequiredFieldsList() {
  let requiredFieldsList =
    [
      'name',
      'year',
      'lengthFeet',
      'bottomContour',
      'fuelTankCapacity',
      'cityAndStateForCapacityTag'
    ];
  if (this.engineType === OUTBOARD) requiredFieldsList.push('widthFeet', 'weightOfFinishedBoat', 'transformHeightAtCenterline');
  if (this.engineType === INBOARD) requiredFieldsList.push('engineAndDriveWeight', 'weightOfUnfinishedBoat');
  return requiredFieldsList;
}

function _initBoatSpecifications(boat) {
  let boatSpecifications = {
    name: boat.name || '',
    year: boat.year || '',
    cityAndStateForCapacityTag: boat.cityAndStateForCapacityTag || '',
    lengthFeet: boat.lengthFeet === undefined ? '' : boat.lengthFeet,
    lengthInches: boat.lengthInches === undefined ? '' : boat.lengthInches,
    bottomContour: boat.bottomContour || '',
    fuelTankCapacity: boat.fuelTankCapacity === undefined ? '' : boat.fuelTankCapacity
  };
  if (this.engineType === OUTBOARD) {
    boatSpecifications.widthFeet = boat.widthFeet === undefined ? '' : boat.widthFeet;
    boatSpecifications.widthInches = boat.widthInches === undefined ? '' : boat.widthInches;
    boatSpecifications.weightOfFinishedBoat = boat.weightOfFinishedBoat === undefined ? '' : boat.weightOfFinishedBoat;
    boatSpecifications.transformHeightAtCenterline = boat.transformHeightAtCenterline === undefined ? '' : boat.transformHeightAtCenterline;
    boatSpecifications.motorType = boat.motorType || SINGLE;
    boatSpecifications.hasElectricPropulsion = !!boat.hasElectricPropulsion;
  }
  if (this.engineType === INBOARD) {
    boatSpecifications.engineAndDriveWeight = boat.engineAndDriveWeight === undefined ? '' : boat.engineAndDriveWeight;
    boatSpecifications.weightOfUnfinishedBoat = boat.weightOfUnfinishedBoat === undefined ? '' : boat.weightOfUnfinishedBoat;
    boatSpecifications.dieselPower = boat.dieselPower || false;
    boatSpecifications.dieselPowerCode = boat.dieselPowerCode || '';
  }

  return boatSpecifications;
}

function _initPersonPowerCapacity(boat) {
  const capacity = {
    plateType: boat.plateType || '',
    calBoatWeight: boat.calBoatWeight === undefined ? '' : boat.calBoatWeight,

    reqMaxNumberOfPersons: boat.reqMaxNumberOfPersons === undefined ? '' : boat.reqMaxNumberOfPersons,
    reqMaxPersonsWeight: boat.reqMaxPersonsWeight === undefined ? '' : boat.reqMaxPersonsWeight,
    reqMaxWeight: boat.reqMaxWeight === undefined ? '' : boat.reqMaxWeight,

    calMaxNumberOfPersons: boat.calMaxNumberOfPersons === undefined ? '' : boat.calMaxNumberOfPersons,
    calMaxPersonsWeight: boat.calMaxPersonsWeight === undefined ? '' : boat.calMaxPersonsWeight,
    calMaxWeight: boat.calMaxWeight === undefined ? '' : boat.calMaxWeight,

    appMaxWeight: boat.appMaxWeight === undefined ? '' : boat.appMaxWeight,
    appMaxNumberOfPersons: boat.appMaxNumberOfPersons === undefined ? '' : boat.appMaxNumberOfPersons,
    appMaxPersonsWeight: boat.appMaxPersonsWeight === undefined ? '' : boat.appMaxPersonsWeight,

    overrideApprovedValues:boat.overrideApprovedValues,
    preferredTagType: boat.preferredTagType,
    useManufacturerEngineWeight: boat.useManufacturerEngineWeight
  };

  if (this.engineType === OUTBOARD) {
    capacity.reqMaxHP = boat.reqMaxHP === undefined ? '' : boat.reqMaxHP;
    capacity.calcMaxHP = boat.calcMaxHP === undefined ? '' : boat.calcMaxHP;
    capacity.apprMaxHP = boat.apprMaxHP === undefined ? '' : boat.apprMaxHP;
    capacity.reqSpecifiedEngineWeight = boat.reqSpecifiedEngineWeight !== undefined
      ? boat.reqSpecifiedEngineWeight
      : (boat.specifiedEngineWeight === undefined || boat.specifiedEngineWeight === null ? '' : boat.specifiedEngineWeight);
  }

  if (boat.dualSteering) {
    capacity.reqMaxHPDualSteering = boat.reqMaxHPDualSteering || '';
    capacity.calcMaxHPDualSteering = boat.calcMaxHPDualSteering || '';
    capacity.apprMaxHPDualSteering = boat.apprMaxHPDualSteering || '';
  }

  return capacity;
}

function _initPowerCapacity(boat) {
  return {
    maxSpeedLessThan30: boat.maxSpeedLessThan30 !== undefined ? boat.maxSpeedLessThan30 : true,
    passesTestAtFullPower: !!boat.passesTestAtFullPower,
    maneuveringSpeed: boat.maneuveringSpeed === undefined ? '' : boat.maneuveringSpeed
  };
}

function _initFloatationCapacity(floatation) {
  if (floatation && floatation.length) {
    return {
      testDate: moment(floatation[0].testDate).utc(),
      files: floatation
    };
  } else {
    return {};
  }
}

function _initDualSteering(boat) {
  if (this.engineType !== OUTBOARD) return undefined;
  return boat.dualSteering !== undefined ? boat.dualSteering : false;
}

function _initCubicCapacity(boat) {
  let cubicCapacity = {};

  if (boat.areas && boat.areas.length !== undefined) {
    let loadedAreas = {};
    boat.areas.forEach(element => {
      loadedAreas[element.type] = element;
    });
    cubicCapacity.areas = loadedAreas;
    cubicCapacity.isAreas =  boat.manualAreas === undefined ? false : boat.manualAreas;
    cubicCapacity.isManual = boat.manualCubicCapacity === undefined ? true : boat.manualCubicCapacity;
  } else if (boat.areas) {
    cubicCapacity.areas = boat.areas;
    cubicCapacity.isAreas =  boat.isAreas === undefined ? false : boat.isAreas;
    cubicCapacity.isManual = boat.isManual === undefined ? true : boat.isManual;
  } else {
    cubicCapacity.areas = {
      [AREA_TYPES.AA_EIGHTH_LENGTH_FORWARD]: {},
      [AREA_TYPES.A_QTR_LENGTH_FORWARD]: {},
      [AREA_TYPES.B_AMIDSHIPS]: {},
      [AREA_TYPES.C_QTR_LENGTH_AFT]: {},
      [AREA_TYPES.D_TRANSOM]: {}
    };
    cubicCapacity.isAreas =  false;
    cubicCapacity.isManual = true;
  }

  cubicCapacity.cubicCapacity = boat.cubicCapacity === undefined ? '' : boat.cubicCapacity;
  cubicCapacity.calculationLength = boat.calculationLength === undefined ? '' : boat.calculationLength;
  cubicCapacity.volumeWithinMotor = boat.volumeWithinMotor === undefined ? '' : boat.volumeWithinMotor;

  return cubicCapacity;
}



// WEBPACK FOOTER //
// ./src/app/store/models/Boat.js